/**
 * Top-left currencies row: gold (ticks up as kill rewards land), Shards and the keystone level,
 * each a small glyph + number. Reads meta/inrunHud's `inRunView` (plain fields, polled per frame)
 * and the world's key level; text is rewritten only when a number changes.
 */
import type { HudUpdate } from '../hud';
import { el, setClass, setText } from '../dom';
import { glyphSvg, groupNumber, intText } from '../kit';
import { inRunView } from '../../meta/inrunHud';
import { currencyGlyphSvg } from '../../meta/ui/glyphs';
import type { HudWidget } from '../widgets';
import { findsCount, findsRows } from '../loot/lootModel'; // +world-resources

/** How long the "+N" gain label stays after the last gain. */
const DELTA_MS = 1600;

export class CurrenciesRow implements HudWidget {
  readonly root: HTMLElement;
  private readonly gold: HTMLElement;
  private readonly goldText: HTMLElement;
  private readonly goldDelta: HTMLElement;
  private readonly shards: HTMLElement;
  private readonly shardText: HTMLElement;
  private readonly keyText: HTMLElement;
  private shownGold = NaN;
  private shownShards = NaN;
  private shownKey = -1;
  /** Gold target at the last frame, the gold the current "+N" counts from, and when it fades. */
  private lastTarget = NaN;
  private deltaBase = 0;
  private deltaUntil = 0;
  private deltaShown = -1;
  private flip = false;

  constructor(host: HTMLElement) {
    const root = (this.root = el('div', 'hk-currencies', host));
    this.gold = el('span', 'hk-currency hk-hit cur-gold', root);
    this.gold.title = 'Gold (this run’s kills and gold picked up are added live; completion bonuses at the end)'; // +econ-pacing
    this.gold.appendChild(currencyGlyphSvg('gold'));
    this.goldText = el('span', 'hk-num', this.gold);
    this.goldDelta = el('span', 'cur-delta hk-num', this.gold);
    this.shards = el('span', 'hk-currency hk-hit cur-shards', root);
    this.shards.title = 'Found in the dungeon: nothing yet. Smash urns and crates, find hidden caches.'; // +world-resources: was the dead Shards counter
    this.shards.appendChild(currencyGlyphSvg('shards'));
    this.shardText = el('span', 'hk-num', this.shards);
    const key = el('span', 'hk-currency hk-hit cur-key', root);
    key.title = 'Keystone level';
    key.appendChild(glyphSvg('key'));
    this.keyText = el('span', 'hk-num', key);
  }

  update(h: HudUpdate, realNow: number): void {
    const v = inRunView;
    setClass(this.gold, 'hk-hidden', !v.active);
    setClass(this.shards, 'hk-hidden', !v.active || !h.world.worldLoot); // +world-resources
    if (v.active) {
      // +econ-pacing: gold picked up in the dungeon (world loot) counts in the purse live too.
      const foundGold = h.world.worldLoot?.collected.gold ?? 0;
      const gold = v.gold + foundGold;
      const goldTarget = v.goldTarget + foundGold;
      if (gold !== this.shownGold) {
        this.shownGold = gold;
        setText(this.goldText, groupNumber(gold));
      }
      // +world-resources: the run's collected world resources (the old Shards counter was always 0).
      const found = h.world.worldLoot?.collected;
      const n = findsCount(found);
      if (n !== this.shownShards) {
        this.shownShards = n;
        setText(this.shardText, groupNumber(n));
        const rows = findsRows(found, h.world.mplus.level).filter((r) => r.d.key !== 'gold');
        this.shards.title = rows.length ? `Found in the dungeon: ${rows.map((r) => `${r.n} ${r.n === 1 ? r.d.one : r.d.label}`).join(', ')}` : 'Found in the dungeon: nothing yet. Smash urns and crates, find hidden caches.';
      }
      // "+N" gain label: counts the gains of a burst of kills, then fades.
      if (Number.isNaN(this.lastTarget) || goldTarget < this.lastTarget) this.lastTarget = this.deltaBase = goldTarget;
      if (goldTarget > this.lastTarget) {
        if (realNow >= this.deltaUntil) this.deltaBase = this.lastTarget;
        this.lastTarget = goldTarget;
        this.deltaUntil = realNow + DELTA_MS;
        // Restart the flash by swapping between two identical animations (no reflow read).
        this.flip = !this.flip;
        setClass(this.goldDelta, 'flash-a', this.flip);
        setClass(this.goldDelta, 'flash-b', !this.flip);
      }
      const live = realNow < this.deltaUntil;
      const d = live ? goldTarget - this.deltaBase : 0;
      if (d !== this.deltaShown) {
        this.deltaShown = d;
        setText(this.goldDelta, d > 0 ? `+${groupNumber(d)}` : '');
      }
      setClass(this.gold, 'ticking', gold !== goldTarget);
    }
    const lvl = h.world.mplus.level;
    if (lvl !== this.shownKey) {
      this.shownKey = lvl;
      setText(this.keyText, `+${intText(lvl)}`);
    }
  }
}
